import React, {useEffect, useState, useCallback} from 'react';

const FinancialForecastPanel = ({
	                                financialForecast, loading, error, showForecastSettings,
	                                setShowForecastSettings
                                }) => {
	const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
	const [sortedFinancialData, setSortedFinancialData] = useState([]);

	const requestSort = (key) => {
		let direction = 'asc';
		if (sortConfig.key === key && sortConfig.direction === 'asc') {
			direction = 'desc';
		}
		setSortConfig({ key, direction });
	};

	const getSortedFinancialData = useCallback(() => {
		if (!financialForecast || !financialForecast.length) return [];

		let sortableItems = [...financialForecast];

		if (sortConfig.key) {
			sortableItems.sort((a, b) => {
				let aValue = a[sortConfig.key];
				let bValue = b[sortConfig.key];

				// Handle null/undefined values
				if (aValue === null || aValue === undefined) aValue = '';
				if (bValue === null || bValue === undefined) bValue = '';

				// Convert to numbers for numeric fields
				const numericFields = ['monthlySalesRate', 'forecasted_demand', 'retail_price', 'cogs',
				                       'monthlyCogs', 'monthlyGross', 'monthlyProfit',
				                       'totalForecastedCogs', 'totalForecastedGross', 'totalForecastedProfit'];

				if (numericFields.includes(sortConfig.key)) {
					// For numeric fields, parse as float
					aValue = parseFloat(aValue) || 0;
					bValue = parseFloat(bValue) || 0;

					// Simple numeric comparison
					if (sortConfig.direction === 'asc') {
						return aValue - bValue;
					} else {
						return bValue - aValue;
					}
				} else {
					// For string fields (like product_name)
					aValue = String(aValue).toLowerCase().trim();
					bValue = String(bValue).toLowerCase().trim();

					// Use localeCompare for better string sorting
					if (sortConfig.direction === 'asc') {
						return aValue.localeCompare(bValue);
					} else {
						return bValue.localeCompare(aValue);
					}
				}
			});
		}

		return sortableItems;
	}, [financialForecast, sortConfig]);

	useEffect(() => {
		setSortedFinancialData(getSortedFinancialData());
	}, [getSortedFinancialData]);

	const getSortIndicator = (key) => {
		if (sortConfig.key === key) {
			return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
		}
		return '';
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
				<p className="font-bold">Error</p>
				<p>{error}</p>
			</div>
		);
	}

	if (!loading && !error && financialForecast.length === 0 && !showForecastSettings) {
		return (
			<div className="bg-white rounded-lg shadow p-8 text-center">
				<svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
				</svg>
				<h3 className="text-lg font-medium text-gray-900 mb-2">No Forecast Data Available</h3>
				<p className="text-gray-500 mb-4">Click the 'Forecast Settings' button to set parameters and generate a supply forecast.</p>
				<button
					onClick={() => setShowForecastSettings(true)}
					className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
				>
					Open Forecast Settings
				</button>
			</div>
		)
	}

	if (!financialForecast.length) {
		return (
			<div className="p-6 text-center">
				<svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
				</svg>
				<h3 className="text-lg font-medium text-gray-900 mb-2">No Financial Forecast Data</h3>
				<p className="text-gray-500">Generate a forecast to view financial metrics.</p>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="overflow-auto relative max-h-[calc(100vh-300px)]">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50 sticky top-0 z-10">
					<tr>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('product_name')}
						>
							Product Name{getSortIndicator('product_name')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('monthlySalesRate')}
						>
							Monthly Sales{getSortIndicator('monthlySalesRate')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('forecasted_demand')}
						>
							Forecasted Demand{getSortIndicator('forecasted_demand')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('retail_price')}
						>
							Unit Price{getSortIndicator('retail_price')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('cogs')}
						>
							Unit Cost{getSortIndicator('cogs')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('monthlyCogs')}
						>
							Monthly COGS{getSortIndicator('monthlyCogs')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('monthlyGross')}
						>
							Monthly Gross{getSortIndicator('monthlyGross')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('monthlyProfit')}
						>
							Monthly Profit{getSortIndicator('monthlyProfit')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('totalForecastedCogs')}
						>
							Total Forecasted COGS{getSortIndicator('totalForecastedCogs')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('totalForecastedGross')}
						>
							Total Forecasted Gross{getSortIndicator('totalForecastedGross')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('totalForecastedProfit')}
						>
							Total Forecasted Profit{getSortIndicator('totalForecastedProfit')}
						</th>
					</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
					{sortedFinancialData.map((item, index) => (
						<tr key={`${item.product_sku}-${index}`} className="hover:bg-gray-50">
							<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
								{item.product_name}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								{parseFloat(item.monthlySalesRate).toFixed(0)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								{parseFloat(item.forecasted_demand).toFixed(0)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								${parseFloat(item.retail_price).toFixed(2)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								${parseFloat(item.cogs).toFixed(2)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								${parseFloat(item.monthlyCogs).toFixed(2)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								${parseFloat(item.monthlyGross).toFixed(2)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								${parseFloat(item.monthlyProfit).toFixed(2)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								${parseFloat(item.totalForecastedCogs).toFixed(2)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								${parseFloat(item.totalForecastedGross).toFixed(2)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								${parseFloat(item.totalForecastedProfit).toFixed(2)}
							</td>
						</tr>
					))}
					</tbody>
					<tfoot className="bg-gray-50">
					<tr>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
							Total/Avg
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							{financialForecast.reduce((sum, item) => sum + parseFloat(item.monthlySalesRate || 0), 0).toFixed(0)}
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							{financialForecast.reduce((sum, item) => sum + parseFloat(item.forecasted_demand || 0), 0).toFixed(0)}
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							{financialForecast.length > 0
							 ? '$' + (financialForecast.reduce((sum, item) => sum + parseFloat(item.retail_price || 0), 0) / financialForecast.length).toFixed(2)
							 : '-'}
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							{financialForecast.length > 0
							 ? '$' + (financialForecast.reduce((sum, item) => sum + parseFloat(item.cogs || 0), 0) / financialForecast.length).toFixed(2)
							 : '-'}
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							${financialForecast.reduce((sum, item) => sum + parseFloat(item.monthlyCogs || 0), 0).toFixed(2)}
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							${financialForecast.reduce((sum, item) => sum + parseFloat(item.monthlyGross || 0), 0).toFixed(2)}
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							${financialForecast.reduce((sum, item) => sum + parseFloat(item.monthlyProfit || 0), 0).toFixed(2)}
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							${financialForecast.reduce((sum, item) => sum + parseFloat(item.totalForecastedCogs || 0), 0).toFixed(2)}
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							${financialForecast.reduce((sum, item) => sum + parseFloat(item.totalForecastedGross || 0), 0).toFixed(2)}
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							${financialForecast.reduce((sum, item) => sum + parseFloat(item.totalForecastedProfit || 0), 0).toFixed(2)}
						</td>
					</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
};

export default FinancialForecastPanel;
